import React, { useState, useRef, useEffect } from 'react';
import { ImageIcon } from 'lucide-react';

interface SafeImageProps {
  src: string;
  alt: string;
  className?: string;
  fallbackSrc?: string;
  lazy?: boolean;
  onLoad?: () => void;
  onError?: () => void;
  webpSrc?: string;
  avifSrc?: string;
}

const SafeImage: React.FC<SafeImageProps> = ({
  src,
  alt,
  className = '',
  fallbackSrc,
  lazy = true,
  onLoad,
  onError,
  webpSrc,
  avifSrc
}) => {
  const [imageState, setImageState] = useState<'loading' | 'loaded' | 'error'>('loading');
  const [currentSrc, setCurrentSrc] = useState(src);
  const [isInView, setIsInView] = useState(!lazy);
  const imgRef = useRef<HTMLImageElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    if (!lazy) return;

    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observerRef.current?.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    if (imgRef.current) {
      observerRef.current.observe(imgRef.current);
    }

    return () => {
      observerRef.current?.disconnect();
    };
  }, [lazy]);

  const handleLoad = () => {
    setImageState('loaded');
    onLoad?.();
  };

  const handleError = () => {
    if (fallbackSrc && currentSrc !== fallbackSrc) {
      setCurrentSrc(fallbackSrc);
      setImageState('loading');
    } else {
      setImageState('error');
      onError?.();
    }
  };

  const Skeleton = () => (
    <div 
      className={`${className} bg-muted/30 animate-pulse flex items-center justify-center`}
      ref={imgRef}
      style={{ aspectRatio: 'inherit' }}
    >
      <ImageIcon className="w-8 h-8 text-muted-foreground/50" />
    </div>
  );

  const ErrorFallback = () => (
    <div 
      className={`${className} bg-muted/20 border border-dashed border-muted-foreground/30 flex items-center justify-center`}
      ref={imgRef}
    >
      <div className="text-center p-4">
        <ImageIcon className="w-6 h-6 text-muted-foreground/50 mx-auto mb-2" />
        <p className="text-xs text-muted-foreground">Imagem indisponível</p>
      </div>
    </div>
  );

  if (!isInView) {
    return <Skeleton />;
  }

  if (imageState === 'error') {
    return <ErrorFallback />;
  }

  // Use modern image formats when available
  if (webpSrc || avifSrc) {
    return (
      <>
        {imageState === 'loading' && <Skeleton />}
        <picture>
          {avifSrc && <source srcSet={avifSrc} type="image/avif" />}
          {webpSrc && <source srcSet={webpSrc} type="image/webp" />}
          <img
            ref={imgRef}
            src={currentSrc}
            alt={alt}
            className={`${className} ${imageState === 'loaded' ? 'opacity-100' : 'opacity-0 absolute'} transition-opacity duration-300`}
            onLoad={handleLoad}
            onError={handleError}
            loading={lazy ? 'lazy' : 'eager'}
            decoding="async"
          />
        </picture>
      </>
    );
  }

  return (
    <>
      {imageState === 'loading' && <Skeleton />}
      <img
        ref={imgRef}
        src={currentSrc}
        alt={alt}
        className={`${className} ${imageState === 'loaded' ? 'opacity-100' : 'opacity-0 absolute'} transition-opacity duration-300`}
        onLoad={handleLoad}
        onError={handleError}
        loading={lazy ? 'lazy' : 'eager'}
        decoding="async"
      />
    </>
  );
};

export default SafeImage;